/**
 * @fileoverview Structured U.S. mailing-address field with an interactive Mapbox
 *   candidate picker and a USPS validation badge, for the shared contact form.
 * @module ui-admin/components/shared-forms
 *
 * Responsibilities:
 * - Render line1/line2/city/state/ZIP inputs bound to a single PostalAddress.
 * - Offer as-you-type Mapbox candidates on line1 ("you typed X → which one?").
 * - Standardize the chosen/typed address via USPS; when USPS suggests a
 *   different form, present a "use suggested vs keep what I entered" choice
 *   (never silently overwrite). Show a status badge once resolved.
 *
 * Dependencies:
 * - react, lucide-react (peer)
 * - @firesquirrel/core-domain (PostalAddress, formatPostalAddress,
 *   postalAddressesDiffer)
 * - useAddressValidation (../../utils/addressValidation) — owns the breakouts
 *
 * Architecture:
 * - Presentation-only React component; no Dexie or direct fetch. All network
 *   work is delegated to useAddressValidation (Mapbox + /api/validate/address).
 * - The Mapbox token is host-injected (never hardcoded). When absent, the field
 *   degrades to plain structured inputs + USPS validation button.
 *
 * Notes:
 * - Replaces the legacy freeform address <input> in ContactInlineForm so the
 *   structured/validated address is available system-wide (#144 Phase 2).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, AlertTriangle, Loader2, MapPin, WifiOff } from 'lucide-react';
import {
  formatPostalAddress,
  postalAddressesDiffer,
  type PostalAddress,
  type AddressCandidate,
  type AddressProximity,
} from '@firesquirrel/core-domain';
import { useAddressValidation } from '../../utils/addressValidation';
import type { GridTheme } from '../../contexts/ThemeContext';

const SUGGEST_DEBOUNCE_MS = 300;
const AUTO_VALIDATE_DEBOUNCE_MS = 1000; // Debounce auto-validation to avoid excessive API calls

const EMPTY_ADDRESS: PostalAddress = {
  line1: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: 'US',
  validationStatus: 'needsValidation',
};

export interface StructuredAddressFieldProps {
  /** Theme for styling consistency with the surrounding form. */
  theme: GridTheme;
  /**
   * Current structured address (controlled); undefined or null renders an
   * empty block. `null` is accepted so callers may hold the "explicitly
   * cleared" sentinel in their form state without a display-time crash.
   */
  value?: PostalAddress | null;
  /** Emits the next address (or undefined when fully cleared). */
  onChange: (next: PostalAddress | undefined) => void;
  /** Public Mapbox client token (host-injected). Omit to disable the picker. */
  mapboxToken?: string;
  /**
   * Optional bias point (e.g. the tenant's department coverage centroid) so
   * as-you-type suggestions near the department rank above equally-valid
   * distant matches. Purely a ranking bias — never excludes results, since a
   * legitimate contact address can be outside the coverage area. Omit to fall
   * back to Mapbox's default (IP-based) ranking.
   */
  mapboxProximity?: AddressProximity;
  /**
   * Two supported modes for the whole application:
   * - `true` (default): Mapbox-assisted typing PLUS USPS standardization —
   *   auto-validates on edit/pick, offers the "use suggested vs keep entered"
   *   prompt, and shows the USPS-verified status badge.
   * - `false`: Mapbox-assisted typing ONLY — suggestions still appear and
   *   populate structured fields on pick, but USPS is never called (no
   *   auto-validate, no manual Validate button, no status badge). Use this
   *   wherever an address needs help being typed correctly but does not need
   *   postal deliverability confirmed.
   */
  requireValidation?: boolean;
  /** Shared label / input / error class tokens from the parent form. */
  labelCls: string;
  inputCls: string;
  errorCls: string;
  /** Layout variant: 'sli' renders full field labels matching the Streamline contact editor */
  variant?: 'standard' | 'sli';
}

/**
 * @component StructuredAddressField
 * Purpose:     Capture + validate a structured U.S. postal address.
 * Props:       theme, value, onChange, mapboxToken, mapboxProximity, requireValidation, labelCls/inputCls/errorCls.
 * Behavior:    line1 typeahead → Mapbox candidates → pick. When
 *              requireValidation (default true): also runs USPS standardize;
 *              when USPS differs, prompt to use the suggestion or keep what was
 *              entered; manual edits clear the validated status + any prompt and
 *              trigger auto-validation after a 1-second debounce. When
 *              requireValidation is false: Mapbox assist only — no USPS call,
 *              no auto-validate, no status badge, no Validate button.
 * Data Source: hybrid (Mapbox + /api/validate/address via useAddressValidation).
 * Constraints: presentation-only; no direct fetch/Dexie; token host-injected.
 * Performance: suggestions debounced (300ms) + auto-validation debounced (1s) + stale-response guarded in hook.
 */
export function StructuredAddressField({
  theme: t,
  value,
  onChange,
  mapboxToken,
  mapboxProximity,
  requireValidation = true,
  labelCls,
  inputCls,
  errorCls,
  variant = 'standard',
}: StructuredAddressFieldProps) {
  const addr = value ?? EMPTY_ADDRESS;
  const { suggestions, suggestStatus, standardizeStatus, suggest, clearSuggestions, standardize } = useAddressValidation();
  const [showSuggestions, setShowSuggestions] = useState(false);
  // When USPS returns a standardized form that differs from what the user
  // entered, hold both versions here so they can choose — never auto-apply.
  const [pendingSuggestion, setPendingSuggestion] = useState<
    { entered: PostalAddress; suggested: PostalAddress } | null
  >(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoValidateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic token so an in-flight standardize whose inputs were edited
  // mid-flight is discarded on resolve instead of resurrecting a stale prompt.
  const standardizeSeq = useRef(0);

  // Cancel any pending typeahead and auto-validation so they can't fire after unmount.
  useEffect(() => () => { 
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (autoValidateRef.current) clearTimeout(autoValidateRef.current);
  }, []);

  const runStandardize = useCallback(async (candidate?: PostalAddress) => {
    const entered = candidate ?? addr;
    const seq = (standardizeSeq.current += 1);
    const result = await standardize(entered);
    // The user edited a field while USPS was responding (patch bumps the
    // token): discard this now-stale result rather than overwrite the edit.
    if (seq !== standardizeSeq.current) return;
    // DFE-018: when USPS returns an empty result, keep the user's entered
    // values instead of clearing all fields. Mark as needing validation so
    // the status badge reflects the unverified state.
    if (!result.line1) {
      onChange({ ...entered, validationStatus: 'needsValidation', validationMessage: undefined, validatedAt: undefined });
      return;
    }
    // result.validatedAt is set ONLY when USPS actually responded (the local /
    // offline fallback never stamps it). Prompt only when USPS produced a
    // standardized form that differs from what the user entered.
    if (result.validatedAt && postalAddressesDiffer(entered, result)) {
      setPendingSuggestion({ entered, suggested: result });
      return;
    }
    setPendingSuggestion(null);
    onChange(result);
  }, [addr, standardize, onChange]);

  // Any manual edit invalidates a prior USPS confirmation (and any open
  // suggestion prompt) until re-validated.
  const patch = useCallback((fields: Partial<PostalAddress>) => {
    standardizeSeq.current += 1;
    setPendingSuggestion(null);
    const next: PostalAddress = {
      ...addr,
      ...fields,
      validationStatus: 'needsValidation',
      validationMessage: undefined,
      validatedAt: undefined,
    };
    const isEmpty = !next.line1 && !next.line2 && !next.city && !next.state && !next.postalCode;
    onChange(isEmpty ? undefined : next);

    // Auto-trigger validation after a debounce delay when fields are edited —
    // only in the USPS-validated mode. Assist-only mode never calls USPS.
    if (autoValidateRef.current) clearTimeout(autoValidateRef.current);
    if (!isEmpty && requireValidation) {
      autoValidateRef.current = setTimeout(() => {
        void runStandardize(next);
      }, AUTO_VALIDATE_DEBOUNCE_MS);
    }
  }, [addr, onChange, runStandardize, requireValidation]);

  const acceptSuggestion = useCallback(() => {
    if (!pendingSuggestion) return;
    onChange(pendingSuggestion.suggested);
    setPendingSuggestion(null);
  }, [pendingSuggestion, onChange]);

  // Keep the user's entered values verbatim, but record that they declined the
  // USPS suggestion so the address reads as "needs review" rather than verified.
  const keepEntered = useCallback(() => {
    if (!pendingSuggestion) return;
    onChange({
      ...pendingSuggestion.entered,
      validationStatus: 'validationWarning',
      validationMessage: 'Kept the address as entered; USPS suggested a standardized version.',
      validatedAt: undefined,
    });
    setPendingSuggestion(null);
  }, [pendingSuggestion, onChange]);

  const handleLine1Change = useCallback((raw: string) => {
    setPendingSuggestion(null);
    patch({ line1: raw });
    setShowSuggestions(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { void suggest(raw, mapboxToken, mapboxProximity); }, SUGGEST_DEBOUNCE_MS);
  }, [patch, suggest, mapboxToken, mapboxProximity]);

  const handlePickCandidate = useCallback((candidate: AddressCandidate) => {
    setShowSuggestions(false);
    setPendingSuggestion(null);
    clearSuggestions();
    onChange(candidate.address);
    // Assist-only mode: Mapbox already gave us a structured address; never
    // call USPS. Validated mode: confirm/standardize via USPS as before.
    if (requireValidation) void runStandardize(candidate.address);
  }, [clearSuggestions, onChange, runStandardize, requireValidation]);

  const status = addr.validationStatus;
  const isChecking = standardizeStatus === 'checking';
  const isOffline = standardizeStatus === 'offline' || suggestStatus === 'offline';
  const hasAddress = Boolean(addr.line1 || addr.city || addr.postalCode);
  const canValidate = Boolean(addr.line1) && !isChecking;

  if (variant === 'sli') {
    return (
      <div className="space-y-2">
        {/* Address Line 1 with Mapbox typeahead */}
        <div>
          <label className={labelCls}>Address Line 1</label>
          <div className="relative mt-1">
            <input
              type="text"
              value={addr.line1}
              onChange={(e) => handleLine1Change(e.target.value)}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              onBlur={() => { setTimeout(() => setShowSuggestions(false), 150); }}
              className={`${inputCls} border-gray-300`}
              placeholder="Address Line 1"
              autoComplete="off"
            />
            {suggestStatus === 'loading' && (
              <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 animate-spin" />
            )}
            {showSuggestions && suggestions.length > 0 && (
              <ul
                className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-md border bg-white shadow-lg"
                style={{ borderColor: t.accent + '50' }}
                role="listbox"
              >
                {suggestions.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); handlePickCandidate(c); }}
                      className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-orange-50"
                    >
                      <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-orange-500" />
                      <span className="text-gray-700">{c.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Address Line 2 */}
        <div>
          <label className={labelCls}>Address Line 2</label>
          <input
            type="text"
            value={addr.line2 ?? ''}
            onChange={(e) => patch({ line2: e.target.value })}
            className={`${inputCls} border-gray-300 mt-1`}
            placeholder="Address Line 2"
            autoComplete="off"
          />
        </div>

        {/* City */}
        <div>
          <label className={labelCls}>City</label>
          <input
            type="text"
            value={addr.city}
            onChange={(e) => patch({ city: e.target.value })}
            className={`${inputCls} border-gray-300 mt-1`}
            placeholder="City"
            autoComplete="off"
          />
        </div>

        {/* State */}
        <div>
          <label className={labelCls}>State</label>
          <input
            type="text"
            value={addr.state}
            onChange={(e) => patch({ state: e.target.value })}
            className={`${inputCls} border-gray-300 mt-1`}
            placeholder="State"
            autoComplete="off"
          />
        </div>

        {/* Zip + Validate Button */}
        <div>
          <label className={labelCls}>Zip</label>
          <div className="flex items-center gap-3 mt-1">
            <input
              type="text"
              value={addr.postalCode}
              onChange={(e) => patch({ postalCode: e.target.value })}
              className={`${inputCls} border-gray-300 flex-1 mt-0`}
              placeholder="Zip"
              autoComplete="off"
            />
            {requireValidation && (
              <button
                type="button"
                onClick={() => void runStandardize()}
                disabled={!canValidate}
                className="px-3 py-2 text-xs font-semibold rounded bg-[#0B3B60] text-white hover:bg-[#082a45] transition-colors disabled:opacity-50 shrink-0 inline-flex items-center gap-1.5"
              >
                {isChecking && <Loader2 className="h-3 w-3 animate-spin" />}
                Validate Address
              </button>
            )}
          </div>
          {requireValidation && hasAddress && (
            <p className={`text-[11px] font-medium mt-1 ${status === 'validated' ? 'text-emerald-600' : 'text-red-600'}`}>
              {status === 'validated'
                ? 'Address Verified'
                : (status === 'validationWarning' && addr.validationMessage) || 'Address Not Verified'}
            </p>
          )}
        </div>

        {requireValidation && pendingSuggestion && (
          <AddressSuggestionCard
            theme={t}
            entered={pendingSuggestion.entered}
            suggested={pendingSuggestion.suggested}
            onUseSuggested={acceptSuggestion}
            onKeepEntered={keepEntered}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className={labelCls}>Mailing address</label>
        {requireValidation && hasAddress && (
          <AddressStatusBadge status={status} isChecking={isChecking} isOffline={isOffline} message={addr.validationMessage} />
        )}
      </div>

      {/* Street (line1) with Mapbox typeahead */}
      <div className="relative mt-1">
        <input
          type="text"
          value={addr.line1}
          onChange={(e) => handleLine1Change(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          onBlur={() => { setTimeout(() => setShowSuggestions(false), 150); }}
          className={`${inputCls} border-gray-300`}
          placeholder="Street address"
          autoComplete="off"
        />
        {suggestStatus === 'loading' && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 animate-spin" />
        )}
        {showSuggestions && suggestions.length > 0 && (
          <ul
            className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-md border bg-white shadow-lg"
            style={{ borderColor: t.accent + '50' }}
            role="listbox"
          >
            {suggestions.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  // onMouseDown (not onClick) so the pick fires before input blur.
                  onMouseDown={(e) => { e.preventDefault(); handlePickCandidate(c); }}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-orange-50"
                >
                  <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-orange-500" />
                  <span className="text-gray-700">{c.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Unit / line2 */}
      <input
        type="text"
        value={addr.line2 ?? ''}
        onChange={(e) => patch({ line2: e.target.value })}
        className={`${inputCls} border-gray-300 mt-1.5`}
        placeholder="Apt, suite, unit (optional)"
        autoComplete="off"
      />

      {/* City / State / ZIP */}
      <div className="grid grid-cols-6 gap-2 mt-1.5">
        <input
          type="text"
          value={addr.city}
          onChange={(e) => patch({ city: e.target.value })}
          className={`${inputCls} border-gray-300 col-span-3`}
          placeholder="City"
          autoComplete="off"
        />
        <input
          type="text"
          value={addr.state}
          onChange={(e) => patch({ state: e.target.value.toUpperCase().slice(0, 2) })}
          className={`${inputCls} border-gray-300 col-span-1`}
          placeholder="ST"
          maxLength={2}
          autoComplete="off"
        />
        <input
          type="text"
          value={addr.postalCode}
          onChange={(e) => patch({ postalCode: e.target.value })}
          className={`${inputCls} border-gray-300 col-span-2`}
          placeholder="ZIP"
          autoComplete="off"
        />
      </div>

      {requireValidation && pendingSuggestion && (
        <AddressSuggestionCard
          theme={t}
          entered={pendingSuggestion.entered}
          suggested={pendingSuggestion.suggested}
          onUseSuggested={acceptSuggestion}
          onKeepEntered={keepEntered}
        />
      )}

      <div className="flex items-center justify-between mt-1.5">
        <p className="text-[10px]" style={{ color: t.textMuted }}>
          {formatPostalAddress(addr).replace(/\n/g, ', ') || 'Enter a mailing address'}
        </p>
        {requireValidation && (
          <button
            type="button"
            onClick={() => void runStandardize()}
            disabled={!canValidate}
            className="px-2.5 py-1 text-[11px] font-medium rounded-sm border transition-colors disabled:opacity-50 inline-flex items-center gap-1"
            style={{ borderColor: t.accent + '60', color: t.accent }}
          >
            {isChecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
            Validate
          </button>
        )}
      </div>
      {requireValidation && !pendingSuggestion && status === 'validationWarning' && addr.validationMessage && (
        <p className={errorCls}>{addr.validationMessage}</p>
      )}
    </div>
  );
}

/**
 * @reusable AddressSuggestionCard
 * Usage: Rendered by StructuredAddressField when USPS returns a standardized
 *   address that differs from what the user entered.
 * Rules: Presentation-only; emits the user's choice via callbacks and never
 *   mutates the address itself. The deliverability note reflects the suggested
 *   address's USPS validationStatus only.
 */
function AddressSuggestionCard({
  theme: t,
  entered,
  suggested,
  onUseSuggested,
  onKeepEntered,
}: {
  theme: GridTheme;
  entered: PostalAddress;
  suggested: PostalAddress;
  onUseSuggested: () => void;
  onKeepEntered: () => void;
}) {
  const deliverable = suggested.validationStatus === 'validated';
  return (
    <div
      data-testid="address-suggestion-card"
      className="mt-2 rounded-md border p-2.5 text-xs"
      style={{ borderColor: t.accent + '70', background: t.accent + '12' }}
    >
      <p className="font-semibold mb-1.5 inline-flex items-center gap-1" style={{ color: t.textPrimary }}>
        <MapPin className="h-3.5 w-3.5" style={{ color: t.accent }} />
        USPS suggested a standardized address
      </p>
      <div className="space-y-1.5">
        <div>
          <span className="block text-[10px] uppercase tracking-wide" style={{ color: t.textMuted }}>
            You entered
          </span>
          <span data-testid="address-suggestion-entered" className="text-gray-600 line-through">
            {formatPostalAddress(entered).replace(/\n/g, ', ')}
          </span>
        </div>
        <div>
          <span className="block text-[10px] uppercase tracking-wide" style={{ color: t.textMuted }}>
            USPS suggests
          </span>
          <span data-testid="address-suggestion-suggested" className="font-medium" style={{ color: t.textPrimary }}>
            {formatPostalAddress(suggested).replace(/\n/g, ', ')}
          </span>
          <span
            className={`mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium ${
              deliverable ? 'text-green-600' : 'text-amber-600'
            }`}
            title={suggested.validationMessage}
          >
            {deliverable ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
            {deliverable ? 'Deliverable (USPS)' : 'Standardized — deliverability not confirmed'}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-2.5">
        <button
          type="button"
          data-testid="address-use-suggested"
          onClick={onUseSuggested}
          className="px-2.5 py-1 text-[11px] font-semibold rounded-sm text-white transition-colors"
          style={{ background: t.accent }}
        >
          Use suggested
        </button>
        <button
          type="button"
          data-testid="address-keep-entered"
          onClick={onKeepEntered}
          className="px-2.5 py-1 text-[11px] font-medium rounded-sm border transition-colors"
          style={{ borderColor: t.cardBorder, color: t.textMuted }}
        >
          Keep what I entered
        </button>
      </div>
    </div>
  );
}

/**
 * @reusable AddressStatusBadge
 * Usage: Rendered by StructuredAddressField beside the field label.
 * Rules: Presentation-only; derives its pill solely from the props passed in.
 */
function AddressStatusBadge({
  status,
  isChecking,
  isOffline,
  message,
}: {
  status: PostalAddress['validationStatus'];
  isChecking: boolean;
  isOffline: boolean;
  message?: string;
}) {
  if (isChecking) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking…
      </span>
    );
  }
  if (isOffline) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-400">
        <WifiOff className="h-3 w-3" /> Offline
      </span>
    );
  }
  if (status === 'validated') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-600" title={message}>
        <CheckCircle className="h-3 w-3" /> USPS verified
      </span>
    );
  }
  if (status === 'validationWarning') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600" title={message}>
        <AlertTriangle className="h-3 w-3" /> Needs review
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-400" title={message}>
      <MapPin className="h-3 w-3" /> Not validated
    </span>
  );
}
